import { Visibility, VisibilityOff } from '@mui/icons-material';
import ArrowBackTwoToneIcon from '@mui/icons-material/ArrowBackTwoTone';
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  Link,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  styled
} from '@mui/material';
import { GoogleLogin } from '@react-oauth/google';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { FC, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { loginWithGoogleSignon } from 'src/services/query-client';
import Logo from 'src/ui-bloom/components/Logo';
import Scrollbar from 'src/ui-bloom/components/Scrollbar';
import useAuth from 'src/ui-bloom/hooks/useAuth';
import * as Yup from 'yup';
import oAuth from '../../auth-service';
import { login } from '../../services/apiService';
import useRefMounted from '../../ui-bloom/hooks/useRefMounted';
const icons = {
  Auth0: '/static/images/logo/auth0.svg',
  FirebaseAuth: '/static/images/logo/firebase.svg',
  JWT: '/static/images/logo/jwt.svg',
  Amplify: '/static/images/logo/amplify.svg'
};

const Content = styled(Box)(
  () => `
    display: flex;
    flex: 1;
    width: 100%;
`
);

const MainContent = styled(Box)(
  ({ theme }) => `
  @media (min-width: ${theme.breakpoints.values.md}px) {
    padding: 0 0 0 440px;
  }
  width: 100%;
  display: flex;
  align-items: center;
`
);

const SidebarWrapper = styled(Box)(
  ({ theme }) => `
    position: fixed;
    left: 0;
    top: 0;
    height: 100%;
    background: ${theme.colors.alpha.white[100]};
    width: 440px;
`
);

const SidebarContent = styled(Box)(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing(0)};
`
);

const CardImg = styled(Card)(
  ({ theme }) => `
    border-radius: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    border: 1px solid ${theme.colors.alpha.black[10]};
    transition: ${theme.transitions.create(['border'])};
    position: absolute;

    &:hover {
      border-color: ${theme.colors.primary.main};
    }
`
);

const TypographyH1 = styled(Typography)(
  ({ theme }) => `
    font-size: ${theme.typography.pxToRem(33)};
`
);

const TypographyPrimary = styled(Typography)(
  ({ theme }) => `
      color: ${theme.colors.alpha.trueWhite[100]};
`
);

const TypographySecondary = styled(Typography)(
  ({ theme }) => `
      color: ${theme.colors.alpha.trueWhite[70]};
`
);

const ListItemTextWrapper = styled(ListItemText)(
  ({ theme }) => `
      color: ${theme.colors.primary.light};
`
);
const ListItemIconWrapper = styled(ListItemIcon)(
  ({ theme }) => `
      color: ${theme.colors.success.main};
      min-width: 32px;
`
);

function LoginCover() {
  const { method } = useAuth() as any;
  const { t }: { t: any } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();
  const returl = localStorage.getItem('ezzy-event-login-returl');

  const handleBack = () => {
    oAuth.goBack();
  };

  return (
    <>
      <Helmet>
        <title>Login - EzzyEvents</title>
      </Helmet>
      <Content>
        <SidebarWrapper
          sx={{
            display: { xs: 'none', md: 'flex' }
          }}
        >
          <Scrollbar>
            <SidebarContent>
              <Box>
                <Box
                  sx={{
                    position: 'relative'
                  }}
                >
                  <img
                    height="100%"
                    width="100%"
                    src="/ezzy/images/bg-cover.png"
                  />
                </Box>
              </Box>
            </SidebarContent>
          </Scrollbar>
        </SidebarWrapper>

        <MainContent>
          <Container
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column'
            }}
            maxWidth="sm"
          >
            <Box>
              <Logo />
            </Box>

            {returl && (
              <Box>
                <Tooltip arrow placement="top" title={t('Go back')}>
                  <IconButton onClick={handleBack} color="primary" sx={{}}>
                    <ArrowBackTwoToneIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <Card
              sx={{
                p: 4,
                my: 4
              }}
            >
              <Box textAlign="center">
                <Typography
                  variant="h2"
                  sx={{
                    mb: 1
                  }}
                >
                  {t('Sign in')}
                </Typography>
                <Typography
                  variant="h4"
                  color="text.secondary"
                  fontWeight="normal"
                  sx={{
                    mb: 3
                  }}
                >
                  {t('Fill in the fields below to sign into your account.')}
                </Typography>
              </Box>
              <LoginForm />
              <Box my={4}>
                <Typography
                  component="span"
                  variant="subtitle2"
                  color="text.primary"
                  fontWeight="bold"
                >
                  {t("Don't have an account, yet?")}
                </Typography>{' '}
                <Box display={{ xs: 'block', md: 'inline-block' }}>
                  <Link component={RouterLink} to="/app/sign-up">
                    <b>Sign up here</b>
                  </Link>
                </Box>
              </Box>

              <Box
                sx={{
                  textAlign: '-webkit-center'
                }}
              >
                <GoogleLogin
                  width={'100%'}
                  onSuccess={async (credentialResponse) => {
                    // console.log(credentialResponse);
                    let done = false;
                    try {
                      const res = await loginWithGoogleSignon(
                        credentialResponse.credential
                      );
                      if (res.data.token && !res.data?.redirectToSignup) {
                        oAuth.setLogInSuccess(res.data.token);
                        oAuth.setSSOLogInSuccess('');
                        done = true;
                      }
                      if (res.data.redirectToSignup) {
                        oAuth.setSSOLogInSuccess(credentialResponse.credential);
                        oAuth.redirectToSignup(res.data.user);
                      }
                    } catch (err) {
                      enqueueSnackbar(t('Invalid credentials'), {
                        variant: 'error'
                      });
                      console.error(err);
                    }
                    if (done) {
                      let requrl = localStorage.getItem(
                        'ezzy-event-login-requrl'
                      );
                      const returl = localStorage.getItem(
                        'ezzy-event-login-returl'
                      );
                      if (requrl) {
                        if (returl) {
                          //append returl as query string
                          requrl =
                            requrl +
                            (requrl.includes('?') ? '&' : '?') +
                            'returl=' +
                            encodeURIComponent(returl);
                        }
                        window.location.href = requrl;
                        localStorage.removeItem('ezzy-event-login-requrl');
                        localStorage.removeItem('ezzy-event-login-returl');
                      } else {
                        oAuth.redirectToHome();
                      }
                    }
                  }}
                  onError={() => {
                    console.log('Login Failed');
                    enqueueSnackbar(t('Invalid credentials'), {
                      variant: 'error'
                    });
                  }}
                  shape="rectangular"
                  useOneTap
                />
              </Box>
            </Card>
          </Container>
        </MainContent>
      </Content>
    </>
  );
}

const LoginForm: FC = () => {
  const isMountedRef = useRefMounted();
  const { t }: { t: any } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);

  // Toggles password visibility
  const handlePwdVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Prevents default behavior on mousedown event for password toggle
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <Formik
      initialValues={{
        email: '',
        password: '',
        terms: true,
        submit: null
      }}
      validationSchema={Yup.object().shape({
        email: Yup.string()
          .email(t('The email provided should be a valid email address'))
          .max(255)
          .required(t('The email field is required')),
        password: Yup.string()
          .max(255)
          .required(t('The password field is required')),
        terms: Yup.boolean().oneOf([true], t('You must agree to our terms and conditions'))
      })}
      onSubmit={async (
        values,
        { setErrors, setStatus, setSubmitting }
      ): Promise<void> => {
        let done = false;

        try {
          // Perform login using provided email and password
          const res = await login(values.email, values.password);

          // Log login success and other details
          console.log('Login successful:', res.data);
          console.log('User email:', values.email);
          
          // Store the token in oAuth
          oAuth.setLogInSuccess(res.data.token);

          // Store the user's id as businessId in localStorage
          if (res.data?.user?.id) {
            localStorage.setItem('businessId', res.data.user.id);
          }

          // Mark done as true to indicate successful login
          done = true;
        } catch (err: any) {
          // Handle error during login
          console.error('Login error:', err);

          // Set form submission state to false
          setSubmitting(false);

          // Display error message to the user
          enqueueSnackbar(t('Invalid credentials'), { variant: 'error' });

          // Check if the component is still mounted before updating the state
          if (isMountedRef.current) {
            setStatus({ success: false });
            setErrors({ submit: err.message || t('An error occurred, please try again.') });
          }
        }

        // Handle post-login actions if login was successful
        if (done) {
          let requrl = localStorage.getItem('ezzy-event-login-requrl');
          const returl = localStorage.getItem('ezzy-event-login-returl');

          // Check if requrl exists and handle redirection
          if (requrl) {
            if (returl) {
              // Append returl as query string to the requrl
              requrl =
                requrl +
                (requrl.includes('?') ? '&' : '?') +
                'returl=' +
                encodeURIComponent(returl);
            }

            // Redirect to the constructed requrl
            window.location.href = requrl;

            // Remove temporary URLs from localStorage after redirection
            localStorage.removeItem('ezzy-event-login-requrl');
            localStorage.removeItem('ezzy-event-login-returl');
          } else {
            // Redirect to the home page if no requrl is present
            oAuth.redirectToHome();
          }

          // Update status to success if the component is still mounted
          if (isMountedRef.current) {
            setStatus({ success: true });
          }
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values
      }): JSX.Element => (
        <form noValidate onSubmit={handleSubmit}>
          <TextField
            error={Boolean(touched.email && errors.email)}
            fullWidth
            margin="normal"
            autoFocus
            helperText={touched.email && errors.email}
            label={t('Email address')}
            name="email"
            onBlur={handleBlur}
            onChange={handleChange}
            type="email"
            value={values.email}
            variant="outlined"
          />
          <TextField
            error={Boolean(touched.password && errors.password)}
            fullWidth
            margin="normal"
            helperText={touched.password && errors.password}
            label={t('Password')}
            name="password"
            onBlur={handleBlur}
            onChange={handleChange}
            type={showPassword ? 'text' : 'password'}
            value={values.password}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onMouseDown={handleMouseDownPassword}
                    onClick={handlePwdVisibility}
                    edge="end"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <Box
            alignItems="center"
            display={{ xs: 'block', md: 'flex' }}
            justifyContent="space-between"
          >
            <Box display={{ xs: 'block', md: 'flex' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.terms}
                    name="terms"
                    color="primary"
                    onChange={handleChange}
                  />
                }
                label={
                  <Typography variant="body2">
                    {t('I accept the')}{' '}
                    <Link component="a" href="#">
                      {t('terms and conditions')}
                    </Link>.
                  </Typography>
                }
              />
            </Box>
            <Link component={RouterLink} to="/app/forgot-password">
              <b>{t('Lost password?')}</b>
            </Link>
          </Box>

          {Boolean(touched.terms && errors.terms) && (
            <FormHelperText error>{errors.terms}</FormHelperText>
          )}

          <Button
            sx={{
              mt: 3
            }}
            color="primary"
            startIcon={isSubmitting ? <CircularProgress size="1rem" /> : null}
            disabled={isSubmitting}
            type="submit"
            fullWidth
            size="large"
            variant="contained"
          >
            {t('Sign in')}
          </Button>
        </form>
      )}
    </Formik>
  );
};


export default LoginCover;
